import { AppBar, Button, Drawer, IconButton, Menu, MenuItem, Toolbar, Typography, useMediaQuery } from '@mui/material';
import { AccountCircle, Dashboard, Description, Logout } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faHome, faBars } from '@fortawesome/free-solid-svg-icons';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router';
import { useTheme } from '@mui/material/styles';
import { TokenContext } from './providers/token/TokenProvider';

// Add responsive CSS for desktop spacer
const responsiveStyles = `
  @media (max-width: 650px) {
    .desktop-spacer {
      display: none !important;
    }
  }
`;

function JrgAppBar() {
  const [open, setOpen] = useState(false);
  /** @type {HTMLElement | null} */
  const [anchorEl, setAnchorEl] = useState(null);
  const { loginWithRedirect, logout, isAuthenticated, isLoading } = useAuth0();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const tokenState = TokenContext.useSelector((state) => state.context);
  const menuOpen = Boolean(anchorEl);

  if (isLoading) {
    return <div>Loading ...</div>;
  }


  const renderDesktopNav = () => (
    <>
      <div style={{
        flexGrow: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <div style={{
          width: '14vw',
        }} className="desktop-spacer"></div>
        <Typography
          variant="h6"
          component="h1"
          sx={{
            textShadow: '1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000',
          }}>
                    The Jackie Gleason
        </Typography>
      </div>

      <Button
        size="large"
        className="mr-2"
        href={'https://docs.google.com/document/d/1xrqve90hloOYGR3RSCvwXJBXq_NQR5kmNfrxKYSveKk/edit?usp=sharing'}
        color="inherit">Résumé</Button>

      <IconButton
        size="large"
        color="inherit"
        target="_blank"
        rel="noopener noreferrer"
        href={'https://github.com/jrgleason'}
        className="mr-1"
      >
        <FontAwesomeIcon icon={faGithub}/>
      </IconButton>

      <IconButton
        size="large"
        color="inherit"
        target="_top"
        rel="noopener noreferrer"
        href={`mailto:jackiegleason+jrgsite@gmail.com`}
        className="mr-1"
      >
        <FontAwesomeIcon icon={faEnvelope}/>
      </IconButton>

      {isAuthenticated && (
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="account-menu"
          aria-haspopup="true"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          color="inherit"
        >
          <AccountCircle />
        </IconButton>
      )}

      {!isAuthenticated && (
        <Button
          variant="outlined"
          color="inherit"
          onClick={async () => {
            try {
              await loginWithRedirect();
            } catch (error) {
              console.error('Login failed:', error);
            }
          }}
          sx={{
            'borderColor': '#c0c5c9',
            '&:hover': {
              backgroundColor: 'rgba(192, 197, 201, 0.1)',
            },
          }}
        >
                    Login
        </Button>
      )}
    </>
  );

  const renderMobileNav = () => (
    <>
      <Typography
        variant="h6"
        component="div"
        className="flex-grow text-center text-base"
        sx={{
          textShadow: '1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000',
        }}
      >
                The Jackie Gleason
      </Typography>
      <IconButton
        size="large"
        color="inherit"
        onClick={() => setOpen(true)}
        className="ml-1"
      >
        <FontAwesomeIcon icon={faBars}/>
      </IconButton>
    </>
  );

  return (
    <React.Fragment>
      <style>{responsiveStyles}</style>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          'backdropFilter': 'blur(10px)',
          'backgroundColor': 'rgba(140, 20, 40, 0.85)',
          'color': '#c0c5c9',
          'zIndex': (theme) => theme.zIndex.drawer + 1,
          'top': 0,
          'minHeight': isMobile ? '56px' : '64px',
          '& .MuiButton-root, & .MuiIconButton-root, & .MuiTypography-root': {
            color: '#c0c5c9',
          },
        }}
      >
        <Toolbar
          sx={{
            minHeight: isMobile ? '56px !important' : '64px !important',
            px: isMobile ? 1 : 2,
          }}
        >
          <IconButton
            size={isMobile ? 'medium' : 'large'}
            color="inherit"
            component={Link}
            to="/"
            className={isMobile ? 'mr-1' : 'mr-2'}
          >
            <FontAwesomeIcon icon={faHome}/>
          </IconButton>

          {isMobile ? renderMobileNav() : renderDesktopNav()}
        </Toolbar>
      </AppBar>

      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          list: {
            'aria-labelledby': 'account-menu',
          },
          paper: {
            elevation: 3,
            sx: {
              'overflow': 'visible',
              'filter': 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              'mt': 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
      >
        <MenuItem
          component={Link}
          to="/dashboard"
          onClick={() => setAnchorEl(null)}
          className="min-w-[150px]"
        >
          <Dashboard className="mr-2 text-xl" />
                    Dashboard
        </MenuItem>
        {tokenState?.isAdmin && (
          <MenuItem
            component={Link}
            to="/admin"
            onClick={() => setAnchorEl(null)}
            className="min-w-[150px]"
          >
            <Dashboard className="mr-2 text-xl" />
                      Admin
          </MenuItem>
        )}
        <MenuItem
          component={Link}
          to="/documents"
          onClick={() => setAnchorEl(null)}
          className="min-w-[150px]"
        >
          <Description className="mr-2 text-xl" />
                    Documents
        </MenuItem>
        <MenuItem
          onClick={async () => {
            setAnchorEl(null);
            try {
              await logout({
                logoutParams: {
                  returnTo: window.location.origin,
                },
              });
            } catch (error) {
              console.error('Logout failed:', error);
            }
          }}
          className="min-w-[150px]"
        >
          <Logout className="mr-2 text-xl" />
                    Logout
        </MenuItem>
      </Menu>
      <Drawer
        open={open}
        anchor="right"
        onClose={() => setOpen(false)}
        sx={{
          'zIndex': (theme) => theme.zIndex.drawer + 2,
          '& .MuiDrawer-paper': {
            width: '250px',
            pt: 2,
            px: 2,
          },
        }}
      >
        <nav style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          padding: '16px 0',
        }}>
          <Typography
            variant="h6"
            component="h2"
            className="mb-2 text-center"
          >
                        Menu
          </Typography>

          <Button
            fullWidth
            href={'https://docs.google.com/document/d/1xrqve90hloOYGR3RSCvwXJBXq_NQR5kmNfrxKYSveKk/edit?usp=sharing'}
            className="justify-start"
          >
                        Résumé
          </Button>

          {isAuthenticated && (
            <Button
              fullWidth
              component={Link}
              to="/dashboard"
              onClick={() => setOpen(false)}
              className="justify-start"
            >
              <Dashboard className="mr-1" />
                            Dashboard
            </Button>
          )}

          {isAuthenticated && tokenState?.isAdmin && (
            <Button
              fullWidth
              component={Link}
              to="/admin"
              onClick={() => setOpen(false)}
              className="justify-start"
            >
                            Admin
            </Button>
          )}

          {isAuthenticated && (
            <Button
              fullWidth
              component={Link}
              to="/documents"
              onClick={() => setOpen(false)}
              className="justify-start"
            >
              <Description className="mr-1" />
                            Documents
            </Button>
          )}

          <Button
            fullWidth
            href={'https://github.com/jrgleason'}
            target="_blank"
            rel="noopener noreferrer"
            className="justify-start"
          >
            <FontAwesomeIcon icon={faGithub} style={{ marginRight: '8px' }}/>
                        GitHub
          </Button>

          <Button
            fullWidth
            href={`mailto:jackiegleason+jrgsite@gmail.com`}
            target="_top"
            rel="noopener noreferrer"
            className="justify-start"
          >
            <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '8px' }}/>
                        Email
          </Button>

          {isAuthenticated ? (
            <Button
              fullWidth
              variant="outlined"
              onClick={async () => {
                try {
                  await logout({
                    logoutParams: {
                      returnTo: window.location.origin,
                    },
                  });
                } catch (error) {
                  console.error('Logout failed:', error);
                }
                setOpen(false);
              }}
              className="mt-2"
            >
                            Logout
            </Button>
          ) : (
            <Button
              fullWidth
              variant="outlined"
              onClick={async () => {
                try {
                  await loginWithRedirect();
                } catch (error) {
                  console.error('Login failed:', error);
                }
                setOpen(false);
              }}
              className="mt-2"
            >
                            Login
            </Button>
          )}
        </nav>
      </Drawer>
    </React.Fragment>
  );
}

export default JrgAppBar;
